import { usePage } from '@inertiajs/vue3';
import {
    initOneSignal,
    requestNotificationPermission,
    getPlayerId,
    canRequestPermission,
    onSubscriptionChange,
} from '@/services/oneSignal.js';

/**
 * Composable: ciclo de vida de OneSignal en el frontend.
 *
 * - init(): carga el SDK al iniciar la app (llamado desde app.js).
 * - enableForUser(): CTA "Activar notificaciones". Retorna un objeto
 *   con el resultado real para que la UI pueda mostrar feedback honesto.
 *
 * Resultados posibles de enableForUser():
 *  - { ok: true,  playerId }            éxito
 *  - { ok: false, reason: 'no-app-id' }        escuela sin OneSignal
 *  - { ok: false, reason: 'no-sw' }            navegador sin Service Worker
 *  - { ok: false, reason: 'no-notifications' }  Notification API no existe
 *  - { ok: false, reason: 'sdk-init-failed' } SDK no se inicializó
 *  - { ok: false, reason: 'permission-denied' } usuario rechazó el permiso
 *  - { ok: false, reason: 'no-player-id' }    OneSignal no asignó player_id
 *  - { ok: false, reason: 'register-failed' } backend rechazó el registro
 *  - { ok: false, reason: 'unknown', error }  cualquier otro caso
 */
export function useOneSignal() {
    const page = usePage();
    const tenant = page.props?.auth?.user?.tenant ?? null;

    function getAppId() {
        if (! tenant?.onesignal_enabled) return null;
        return tenant.onesignal_app_id || null;
    }

    function getUser() {
        return page.props?.auth?.user ?? null;
    }

    async function ensureReady() {
        if (! getAppId()) return { ok: false, reason: 'no-app-id' };
        if (! ('serviceWorker' in navigator)) return { ok: false, reason: 'no-sw' };
        if (! ('Notification' in window)) return { ok: false, reason: 'no-notifications' };

        try {
            await initOneSignal({
                appId: getAppId(),
                externalUserId: getUser()?.id,
            });
        } catch (e) {
            return { ok: false, reason: 'sdk-init-failed', error: e?.message };
        }

        if (! window.OneSignal) return { ok: false, reason: 'sdk-init-failed' };
        if (! canRequestPermission()) return { ok: false, reason: 'no-notifications' };
        return { ok: true };
    }

    async function init({ user } = {}) {
        const ready = await ensureReady();
        if (! ready.ok) {
            console.info('[OneSignal] init omitido:', ready.reason);
            return;
        }

        onSubscriptionChange(async ({ playerId, optedIn }) => {
            if (optedIn && playerId) {
                await syncPlayerId(playerId);
            }
        });

        const playerId = await getPlayerId();
        if (playerId) {
            await syncPlayerId(playerId);
        }
    }

    async function enableForUser() {
        const ready = await ensureReady();
        if (! ready.ok) return ready;

        const granted = await requestNotificationPermission();
        if (! granted) return { ok: false, reason: 'permission-denied' };

        // requestPermission puede resolver antes de que OneSignal asigne
        // el player_id. Pequeño reintento si el primer getPlayerId falla.
        let playerId = await getPlayerId();
        if (! playerId) {
            await new Promise((r) => setTimeout(r, 600));
            playerId = await getPlayerId();
        }
        if (! playerId) return { ok: false, reason: 'no-player-id' };

        const synced = await syncPlayerId(playerId);
        if (! synced) return { ok: false, reason: 'register-failed' };

        return { ok: true, playerId };
    }

    async function syncPlayerId(playerId) {
        try {
            const response = await window.axios.post('/api/onesignal/register', {
                player_id: playerId,
                device_type: 'web',
            });
            return response.status >= 200 && response.status < 300;
        } catch (e) {
            console.warn('[useOneSignal] register failed:', e?.response?.status, e?.message);
            return false;
        }
    }

    return {
        init,
        enableForUser,
        isConfiguredForTenant: () => Boolean(getAppId()),
    };
}
